import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { useCoachEpicEnabledState } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import Help from 'bundles/common/components/Help';
import EnterprisePageHeader from 'bundles/composable-page-header-enterprise/header-renderers/EnterprisePageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import EnterpriseModalManager from 'bundles/unified-common/components/EnterpriseModalManager';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import useEnterprisePageHeaderLinks from 'bundles/unified-common/hooks/useEnterprisePageHeaderLinks';

/*
Use this to add the header/footer to a page in the enterprise context.

Ensure this is nested beneath the following providers: bundles/unified-common/providers/EnterpriseProductFeaturesDataProvider,
bundles/unified-common/providers/EnterpriseProgramDataProvider and bundles/unified-common/providers/ThirdPartyOrganizationDataProvider.
*/
const EnterpriseHeaderFooterWrapper = ({ children }: { children: React.ReactNode }) => {
  const program = useProgram();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { pathname } = useLocation();

  /*
  We use useShouldShowCoachQuery to determine if we should render the help button. Note, this is not the global help button.
  useChatWidgetSelector doesn't handle this case because it doesn't differentiate between "loading" and "don't show any of the buttons", this is why useShouldShowCoachQuery is used instead.

  The last condition in renderHelpButton ensures it is falsy on CA pages, allowing ProgramHomeWrapper to render a single help icon / avoid overlapping buttons in the bottom right corner.
  */
  const { showEmbeddedCoach } = useCoachEpicEnabledState();
  const isCoachEnabled = showEmbeddedCoach;

  const renderHelpButton = !isCoachEnabled && !pathname.startsWith('/career-academy');

  const pageHeaderLinks = useEnterprisePageHeaderLinks();

  return (
    <React.Fragment>
      <EnterprisePageHeader subnavLinks={pageHeaderLinks} backupProgram={program} backupThirdPartyOrg={thirdPartyOrg} />
      <UniversalPageFeatures authModalOptions={{ isEnterprise: true, programId: program?.id }} />
      <main>{children}</main>
      <PageFooter />
      <EnterpriseModalManager />
      {renderHelpButton && <Help style={{ minWidth: 0, bottom: 12, right: 12 }} isEnterprise />}
    </React.Fragment>
  );
};

export default EnterpriseHeaderFooterWrapper;
